import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import Footer from '@/components/Footer';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen theme-bg">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-amber-700 hover:text-amber-900 hover:bg-amber-100 btn-glow rounded-lg"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <Card className="card-glow p-8 bg-white shadow-lg">
          <h1 className="text-4xl font-bold text-amber-900 mb-4">Terms of Service</h1>
          <p className="text-sm text-gray-600 mb-8">Last Updated: December 2025</p>

          <div className="prose prose-amber max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">1. Agreement to Terms</h2>
              <p className="text-gray-700">
                By accessing or using Lyceum ("Service"), you agree to be bound by these Terms of Service 
                ("Terms"). If you disagree with any part of these terms, you may not access the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">2. Description of Service</h2>
              <p className="text-gray-700">
                Lyceum provides AI-powered educational content for history learning, including personalized 
                courses, interactive debates, and AI-assisted study tools. We reserve the right to modify, suspend, 
                or discontinue any part of the Service at any time.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">3. Accuracy Disclaimer</h2>
              <p className="text-gray-700">
                <strong>AI-Generated Content:</strong> This course content is generated by AI and aims for 
                approximately 95% historical accuracy based on established scholarship. While our system uses 
                reputable sources and strives for factual correctness, minor factual errors or outdated information 
                may occur. For academic or professional purposes, please verify critical facts with primary and 
                secondary sources. Lyceum is designed as an educational tool to facilitate learning and should 
                not be considered a substitute for peer-reviewed academic research or professional historical consultation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">4. User Accounts</h2>
              <p className="text-gray-700 mb-2">When you create an account with us, you must provide accurate and complete information. You are responsible for:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Maintaining the security of your account and password</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">5. Subscription and Billing</h2>
              <p className="text-gray-700 mb-2">
                <strong>Free Trial:</strong> New users receive a 7-day free trial. Your subscription will automatically 
                begin after the trial period unless cancelled.
              </p>
              <p className="text-gray-700 mb-2">
                <strong>Billing:</strong> Subscription fees are billed in advance on a monthly or annual basis. 
                Prices are in US Dollars and are subject to change with 30 days notice.
              </p>
              <p className="text-gray-700">
                <strong>Cancellation:</strong> You may cancel your subscription at any time from your profile settings. 
                Cancellation will take effect at the end of the current billing period.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">6. Refund Policy</h2>
              <p className="text-gray-700">
                We offer refunds on a case-by-case basis. Please contact us at help@mylyceum.org within 
                14 days of your purchase to request a refund. Refunds are not available for partial months.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">7. Acceptable Use</h2>
              <p className="text-gray-700 mb-2">You agree not to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Use the Service for any illegal purpose</li>
                <li>Attempt to gain unauthorized access to any part of the Service</li>
                <li>Share your account credentials with others</li>
                <li>Reverse engineer or attempt to extract source code</li>
                <li>Use automated systems to access the Service without permission</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">8. Intellectual Property</h2>
              <p className="text-gray-700">
                The Service and its original content, features, and functionality are owned by Lyceum and are 
                protected by international copyright, trademark, and other intellectual property laws. AI-generated 
                content created through the Service is provided for your personal educational use.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">9. User Content</h2>
              <p className="text-gray-700">
                You retain ownership of any content you create using the Service. By using the Service, you grant 
                us a license to use, store, and process your content solely for the purpose of providing the Service 
                to you.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">10. Disclaimer of Warranties</h2>
              <p className="text-gray-700">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS 
                OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES 
                OR OTHER HARMFUL COMPONENTS.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">11. Limitation of Liability</h2>
              <p className="text-gray-700">
                IN NO EVENT SHALL HISTORYHUB BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR 
                PUNITIVE DAMAGES ARISING OUT OF OR RELATING TO YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN ADVISED 
                OF THE POSSIBILITY OF SUCH DAMAGES.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">12. Governing Law</h2>
              <p className="text-gray-700">
                These Terms shall be governed by and construed in accordance with the laws of the Commonwealth of 
                Virginia, United States, without regard to its conflict of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">13. Changes to Terms</h2>
              <p className="text-gray-700">
                We reserve the right to modify these Terms at any time. We will notify users of any material changes 
                by email or through the Service. Your continued use of the Service after such modifications constitutes 
                acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">14. Contact Information</h2>
              <p className="text-gray-700">
                If you have any questions about these Terms, please contact us at:
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Email:</strong> help@mylyceum.org<br />
                <strong>Service:</strong> Lyceum
              </p>
            </section>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default TermsOfService;
